// ═══════════════════════════════════════════════════════════════════════════
//  ExamsManager.jsx — Fase 2: administrar EXÁMENES (fijo o por plano)
//
//  Vive dentro de ExamsCmsTab (vista «Exámenes»). Crea, edita, publica y borra
//  exámenes de nivel. Cada examen elige su modo:
//    · plano (blueprint): reglas «X de reading A2…», el servidor arma uno cada vez
//    · fijo (fixed): la lista exacta de reactivos, igual para todos
//  Publicar exige que el banco pueda armarlo (factibilidad).
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';

function cab() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` };
}
const API = '/api/admin/assessment';

const VACIO_EXAM = {
  id: null, code: '', title: '', description: '', cefr_target: 'A1',
  mode: 'blueprint', item_ids: [], blueprint: { sections: [] },
  is_published: false, time_limit_seconds: null, pass_score: null,
};

function loQueDijo(d) {
  if (!d) return 'Ocurrió un error.';
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) return d.detail.map((e) => e.msg || '').join(' · ') || 'Datos inválidos.';
  return d.message || 'Ocurrió un error.';
}

export default function ExamsManager({ salud, esquema, onAviso }) {
  const [exams, setExams] = useState([]);
  const [editando, setEditando] = useState(null);
  const [preview, setPreview] = useState(null);
  const [cargando, setCargando] = useState(false);

  const CEFR = esquema?.cefr || ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const SKILLS = esquema?.skills || [];
  const FORMATOS = esquema?.formatos || {};
  const matriz = salud?.matriz || {};

  const carga = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API}/exams`, { headers: cab() });
      const d = await r.json();
      setExams(d.exams || []);
    } catch { onAviso?.({ tipo: 'error', txt: 'No se pudieron cargar los exámenes.' }); }
    setCargando(false);
  }, [onAviso]);

  useEffect(() => { carga(); }, [carga]);

  async function guarda() {
    const esNuevo = !editando.id;
    const cuerpo = esNuevo
      ? {
          code: editando.code.trim(), title: editando.title, description: editando.description || null,
          cefr_target: editando.cefr_target, mode: editando.mode,
          item_ids: editando.item_ids, blueprint: editando.blueprint,
          time_limit_seconds: editando.time_limit_seconds, pass_score: editando.pass_score,
        }
      : {
          code: editando.code.trim(), title: editando.title, description: editando.description || null,
          cefr_target: editando.cefr_target, mode: editando.mode,
          item_ids: editando.item_ids, blueprint: editando.blueprint,
          is_published: editando.is_published,
          time_limit_seconds: editando.time_limit_seconds, pass_score: editando.pass_score,
        };
    try {
      const r = await fetch(esNuevo ? `${API}/exams` : `${API}/exams/${editando.id}`,
        { method: esNuevo ? 'POST' : 'PUT', headers: cab(), body: JSON.stringify(cuerpo) });
      const d = await r.json();
      if (!r.ok) { onAviso?.({ tipo: 'error', txt: loQueDijo(d) }); return; }
      onAviso?.({ tipo: 'ok', txt: esNuevo ? 'Examen creado (despublicado).' : 'Examen actualizado.' });
      setEditando(null); carga();
    } catch { onAviso?.({ tipo: 'error', txt: 'Fallo de conexión al guardar.' }); }
  }

  async function publica(exam, valor) {
    try {
      const r = await fetch(`${API}/exams/${exam.id}`, {
        method: 'PUT', headers: cab(), body: JSON.stringify({ is_published: valor }),
      });
      const d = await r.json();
      if (!r.ok) { onAviso?.({ tipo: 'error', txt: loQueDijo(d) }); return; }
      onAviso?.({ tipo: 'ok', txt: valor ? 'Examen publicado.' : 'Examen despublicado.' });
      carga();
    } catch { onAviso?.({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }

  async function borra(exam) {
    if (!window.confirm(`¿Borrar ${exam.code}?`)) return;
    try {
      const r = await fetch(`${API}/exams/${exam.id}`, { method: 'DELETE', headers: cab() });
      const d = await r.json();
      if (!r.ok) { onAviso?.({ tipo: 'error', txt: loQueDijo(d) }); return; }
      onAviso?.({ tipo: 'ok', txt: `${exam.code} borrado.` });
      carga();
    } catch { onAviso?.({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }

  async function verPreview(exam) {
    try {
      const r = await fetch(`${API}/exams/${exam.id}/preview`, { headers: cab() });
      const d = await r.json();
      if (!r.ok) { onAviso?.({ tipo: 'error', txt: loQueDijo(d) }); return; }
      setPreview({ exam, ...d });
    } catch { onAviso?.({ tipo: 'error', txt: 'Fallo de conexión.' }); }
  }

  return (
    <div className="ecms-exams">
      <div className="ecms-exams-top">
        <p className="ecms-sub">
          Exámenes de nivel A1–C2. El <b>plano</b> arma uno distinto cada vez desde el banco;
          el <b>fijo</b> usa siempre los mismos reactivos. Solo se publica lo que el banco puede armar.
        </p>
        <button className="ecms-btn ecms-btn-primario" onClick={() => setEditando({ ...VACIO_EXAM })}>+ Nuevo examen</button>
      </div>

      {cargando ? <div className="ecms-vacio">Cargando…</div>
        : !exams.length ? <div className="ecms-vacio">Aún no hay exámenes. Crea el primero.</div>
        : (
          <div className="ecms-exam-grid">
            {exams.map((e) => (
              <div key={e.id} className={`ecms-exam-card ${e.is_published ? 'ecms-exam-pub' : ''}`}>
                <div className="ecms-exam-card-top">
                  <span className="ecms-code">{e.code}</span>
                  <span className="ecms-nivel">{e.cefr_target}</span>
                  <span className={`ecms-badge ${e.is_published ? 'ecms-badge-on' : 'ecms-badge-off'}`}>
                    {e.is_published ? 'Publicado' : 'Borrador'}
                  </span>
                </div>
                <div className="ecms-exam-title">{e.title}</div>
                <div className="ecms-exam-meta">
                  {e.mode === 'fixed' ? `Fijo · ${e.item_ids.length} reactivos`
                    : `Plano · ${(e.blueprint.sections || []).reduce((a, s) => a + s.count, 0)} reactivos`}
                </div>
                <div className="ecms-exam-acc">
                  <button className="ecms-mini" onClick={() => verPreview(e)}>Previsualizar</button>
                  <button className="ecms-mini" onClick={() => setEditando(JSON.parse(JSON.stringify(e)))}>Editar</button>
                  {e.is_published
                    ? <button className="ecms-mini ecms-mini-off" onClick={() => publica(e, false)}>Despublicar</button>
                    : <button className="ecms-mini ecms-mini-pub" onClick={() => publica(e, true)}>Publicar</button>}
                  <button className="ecms-mini ecms-mini-off" onClick={() => borra(e)}>Borrar</button>
                </div>
              </div>
            ))}
          </div>
        )}

      {editando && (
        <EditorExamen editando={editando} setEditando={setEditando}
          CEFR={CEFR} SKILLS={SKILLS} FORMATOS={FORMATOS} matriz={matriz}
          onGuardar={guarda} onCerrar={() => setEditando(null)} />
      )}
      {preview && <PreviewExamen preview={preview} onCerrar={() => setPreview(null)} />}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Editor de examen
// ───────────────────────────────────────────────────────────────────────────
function EditorExamen({ editando, setEditando, CEFR, SKILLS, FORMATOS, matriz, onGuardar, onCerrar }) {
  const set = (k, v) => setEditando((e) => ({ ...e, [k]: v }));
  const secciones = editando.blueprint?.sections || [];

  const setSec = (i, campo, valor) => setEditando((e) => {
    const s = [...(e.blueprint.sections || [])];
    s[i] = { ...s[i], [campo]: campo === 'count' ? Number(valor) : valor };
    return { ...e, blueprint: { ...e.blueprint, sections: s } };
  });
  const addSec = () => setEditando((e) => ({
    ...e, blueprint: { ...e.blueprint, sections: [...(e.blueprint.sections || []), { skill: SKILLS[0]?.id || 'reading', cefr_level: e.cefr_target, count: 2 }] },
  }));
  const delSec = (i) => setEditando((e) => ({
    ...e, blueprint: { ...e.blueprint, sections: e.blueprint.sections.filter((_, j) => j !== i) },
  }));

  const disp = (skill, cefr) => (matriz[skill]?.[cefr] ?? 0);
  const totalPlano = secciones.reduce((a, s) => a + (s.count || 0), 0);

  return (
    <div className="ecms-modal" onClick={onCerrar}>
      <div className="ecms-editor" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <h3>{editando.id ? `Editar ${editando.code}` : 'Nuevo examen'}</h3>

        <div className="ecms-row3">
          <label className="ecms-lbl">Código único
            <input className="ecms-inp" value={editando.code} maxLength={24}
              placeholder="EXAM-A2-01" onChange={(e) => set('code', e.target.value)} />
          </label>
          <label className="ecms-lbl">Nivel objetivo
            <select className="ecms-inp" value={editando.cefr_target} onChange={(e) => set('cefr_target', e.target.value)}>
              {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="ecms-lbl">Modo
            <select className="ecms-inp" value={editando.mode} onChange={(e) => set('mode', e.target.value)}>
              <option value="blueprint">Plano (arma al azar)</option>
              <option value="fixed">Fijo (reactivos exactos)</option>
            </select>
          </label>
        </div>

        <label className="ecms-lbl">Título
          <input className="ecms-inp" value={editando.title} maxLength={120}
            onChange={(e) => set('title', e.target.value)} />
        </label>
        <label className="ecms-lbl">Descripción (opcional)
          <textarea className="ecms-inp ecms-area" value={editando.description || ''}
            onChange={(e) => set('description', e.target.value)} />
        </label>

        {editando.mode === 'blueprint' ? (
          <div className="ecms-secs">
            <div className="ecms-opts-tit">Secciones del plano · el banco debe tener suficientes</div>
            {secciones.map((s, i) => {
              const d = disp(s.skill, s.cefr_level);
              const falta = s.count > d;
              return (
                <div key={i} className="ecms-sec">
                  <select className="ecms-inp" value={s.skill} onChange={(e) => setSec(i, 'skill', e.target.value)}>
                    {SKILLS.map((sk) => <option key={sk.id} value={sk.id}>{sk.label}</option>)}
                  </select>
                  <select className="ecms-inp" value={s.cefr_level} onChange={(e) => setSec(i, 'cefr_level', e.target.value)}>
                    {CEFR.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input className="ecms-inp" type="number" min="1" max="50" value={s.count}
                    onChange={(e) => setSec(i, 'count', e.target.value)} style={{ width: 64 }} />
                  <span className={`ecms-disp ${falta ? 'ecms-disp-falta' : ''}`}>
                    hay {d}{falta ? ' ⚠' : ' ✓'}
                  </span>
                  <button className="ecms-mini ecms-mini-off" onClick={() => delSec(i)}>×</button>
                </div>
              );
            })}
            <button className="ecms-mini" onClick={addSec}>+ sección</button>
            <div className="ecms-total">Total: {totalPlano} reactivos por intento</div>
          </div>
        ) : (
          <SelectorFijo editando={editando} setEditando={setEditando} />
        )}

        <div className="ecms-row3">
          <label className="ecms-lbl">Límite de tiempo (seg, opcional)
            <input className="ecms-inp" type="number" min="0" value={editando.time_limit_seconds || ''}
              onChange={(e) => set('time_limit_seconds', e.target.value ? Number(e.target.value) : null)} />
          </label>
          <label className="ecms-lbl">Nota de corte (0–100, opcional)
            <input className="ecms-inp" type="number" min="0" max="100" value={editando.pass_score ?? ''}
              onChange={(e) => set('pass_score', e.target.value ? Number(e.target.value) : null)} />
          </label>
          {editando.id && (
            <label className="ecms-lbl ecms-lbl-check">
              <input type="checkbox" checked={editando.is_published}
                onChange={(e) => set('is_published', e.target.checked)} />
              Publicado
            </label>
          )}
        </div>

        <div className="ecms-editor-botones">
          <button className="ecms-btn" onClick={onCerrar}>Cancelar</button>
          <button className="ecms-btn ecms-btn-primario" onClick={onGuardar}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// Selector de reactivos para el modo FIJO
function SelectorFijo({ editando, setEditando }) {
  const [busca, setBusca] = useState('');
  const [banco, setBanco] = useState([]);
  useEffect(() => {
    const p = new URLSearchParams({ limite: '200' });
    if (busca) p.set('buscar', busca);
    fetch(`${API}/items?${p}`, { headers: cab() }).then((r) => r.json()).then((d) => setBanco(d.items || [])).catch(() => {});
  }, [busca]);

  const ids = editando.item_ids || [];
  const toggle = (id) => setEditando((e) => {
    const yaEsta = (e.item_ids || []).includes(id);
    return { ...e, item_ids: yaEsta ? e.item_ids.filter((x) => x !== id) : [...(e.item_ids || []), id] };
  });
  const porId = Object.fromEntries(banco.map((b) => [b.id, b]));

  return (
    <div className="ecms-fijo">
      <div className="ecms-opts-tit">Reactivos elegidos ({ids.length}) · en este orden</div>
      <div className="ecms-fijo-chips">
        {ids.length === 0 ? <span className="ecms-dim">Ninguno todavía. Elígelos abajo.</span>
          : ids.map((id) => (
            <button key={id} className="ecms-chip-sel" onClick={() => toggle(id)} title="Quitar">
              {porId[id]?.code || `#${id}`} ×
            </button>
          ))}
      </div>
      <input className="ecms-inp" placeholder="Buscar reactivos por código…" value={busca}
        onChange={(e) => setBusca(e.target.value)} />
      <div className="ecms-fijo-lista">
        {banco.map((b) => (
          <button key={b.id} className={`ecms-fijo-item ${ids.includes(b.id) ? 'ecms-fijo-on' : ''}`}
            onClick={() => toggle(b.id)}>
            <span className="ecms-code">{b.code}</span>
            <span className="ecms-dim">{b.skill_label} · {b.cefr_level} · {b.format_label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Vista previa del examen armado
// ───────────────────────────────────────────────────────────────────────────
function PreviewExamen({ preview, onCerrar }) {
  const fact = preview.exam?.factibilidad || preview.exam?.factibilidad;
  return (
    <div className="ecms-modal" onClick={onCerrar}>
      <div className="ecms-editor" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <h3>Vista previa · {preview.exam.code}</h3>
        <p className="ecms-sub">
          {preview.n_reactivos} reactivos · {preview.exam.mode === 'blueprint'
            ? 'una muestra distinta cada intento' : 'siempre los mismos, en orden'}
        </p>
        <div className="ecms-preview-lista">
          {preview.reactivos.map((it, i) => (
            <div key={i} className="ecms-preview-row">
              <span className="ecms-nivel">{it.cefr_level}</span>
              <span className="ecms-dim">{it.skill_label} · {it.format_label}</span>
              <span className="ecms-preview-enun">
                {it.payload?.question || it.payload?.prompt || it.payload?.text
                  || (it.payload?.words || []).join(' ') || (it.payload?.chips || []).join(' ') || '—'}
              </span>
            </div>
          ))}
        </div>
        <div className="ecms-editor-botones">
          <button className="ecms-btn ecms-btn-primario" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
